import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import useTracking from '@gameday/utils/hooks/useTracking';
import buildAudioUrl from '@gameday/utils/buildAudioUrl';
import buildTvUrl from '@gameday/utils/buildTvUrl';
import { getDeviceInteraction } from '@gameday/utils/selectors/getDeviceInteraction';
import { resourcePath } from '@gameday/utils/resource-path';
import { getLanguagePath } from '@gameday/utils/languageHelpers';
import { getGameContextState } from '../store/selectors/gameContextSelector';

export const buttonTypes = {
    PREVIEW: 'preview',
    TICKETS: 'tickets',
    MLBTV: 'mlbtv',
    AUDIO: 'audio',
    GAMEDAY: 'gameday',
    VIDEOS: 'videos',
    WRAP: 'wrap',
    BOX: 'box',
    STORY: 'story'
};

const buttonConfigs = {
    P: ['tickets', 'preview'],
    L: ['gameday', 'mlbtv', 'audio', 'story'],
    F: ['wrap', 'box', 'videos', 'story']
};

const previewButton = ({ gamePk, deviceInteraction, trackButtonClick, languagePath }) => {
    const previewURL = resourcePath.get('gamedayLink', { game_pk: gamePk, languagePath });
    if (!previewURL) {
        return;
    }
    return {
        id: 'preview',
        labelKey: 'button_preview',
        labelFallback: 'Preview',
        url: previewURL,
        handlers: {
            onClick: (e) => {
                if (deviceInteraction?.gameday({ gamePk })) {
                    e.preventDefault();
                }
                trackButtonClick(e);
            }
        }
    };
};

const gamedayButton = ({ gamePk, deviceInteraction, trackButtonClick, languagePath }) => {
    const gamedayURL = resourcePath.get('gamedayLink', { game_pk: gamePk, languagePath });
    if (!gamedayURL) {
        return;
    }
    return {
        id: 'gameday',
        labelKey: 'button_gameday',
        labelFallback: 'Gameday',
        url: gamedayURL,
        handlers: {
            onClick: (e) => {
                if (deviceInteraction?.gameday({ gamePk })) {
                    e.preventDefault();
                }
                trackButtonClick(e);
            }
        }
    };
};

const mlbtvButton = ({ gamePk, gameDate, gamedayContext, tvFeeds, homeAwayContext, deviceInteraction, trackButtonClick, language }) => {
    if (language === 'ja') { // hide TV on JA
        return;
    }

    const { national: nationalFeed } = tvFeeds || {};
    const watchFeed = nationalFeed?.length > 0 ? nationalFeed : tvFeeds[homeAwayContext];
    if (!watchFeed || watchFeed?.length === 0) { // if there's no TV feeds
        return;
    }

    const { mediaId, mediaState } = watchFeed[0];
    if (mediaState !== 'MEDIA_ON' && mediaState !== 'MEDIA_ARCHIVE') { // if the feed exists but isn't on
        return;
    }

    const tvURL = buildTvUrl({ gamePk, date: gameDate, mediaId }, 'America/New_York', gamedayContext, language);
    if (!tvURL) {
        return;
    }

    return {
        id: 'mlbtv',
        labelKey: 'button_mlbtv',
        labelFallback: 'MLB.TV',
        url: tvURL,
        handlers: {
            onClick: (e) => {
                if (deviceInteraction?.tvapp({ gamePk, date: gameDate })) {
                    e.preventDefault();
                }
                trackButtonClick(e);
            }
        }
    };
};

const audioButton = ({ gamePk, audioFeed, gamedayContext, deviceInteraction, trackButtonClick, language }) => {
    const localFeed = audioFeed.find(feed => feed?.language === language) || audioFeed?.[0];
    const { callSign, mediaId, mediaState } = localFeed || {};
    if (mediaState !== 'MEDIA_ON' && mediaState !== 'MEDIA_ARCHIVE') { // if the feed exists but isn't on
        return;
    }

    const audioURL = buildAudioUrl({ gamePk, callSign }, gamedayContext);
    if (!audioURL || !callSign) {
        return;
    }
    return {
        id: 'audio',
        labelKey: 'button_audio',
        labelFallback: 'Audio',
        url: audioURL,
        handlers: {
            onClick: (e) => {
                if (deviceInteraction?.audio({ aid: mediaId })) {
                    e.preventDefault();
                }
                trackButtonClick(e);
            }
        }
    };
};

const wrapButton = ({ gamePk, trackButtonClick, languagePath }) => {
    const wrapURL = resourcePath.get('gamedayWrapLink', { game_pk: gamePk, languagePath });
    if (!wrapURL) {
        return;
    }
    return {
        id: 'wrap',
        labelKey: 'button_wrap',
        labelFallback: 'Wrap',
        url: wrapURL,
        handlers: {
            onClick: (e) => trackButtonClick(e)
        }
    };
};

const boxButton = ({ gamePk, trackButtonClick, languagePath }) => {
    const boxURL = resourcePath.get('gamedayBoxLink', { game_pk: gamePk, languagePath });
    if (!boxURL) {
        return;
    }
    return {
        id: 'box',
        labelKey: 'button_box',
        labelFallback: 'Box',
        url: boxURL,
        handlers: {
            onClick: (e) => trackButtonClick(e)
        }
    };
};

const videoButton = ({ gamePk, trackButtonClick, languagePath }) => {
    const videoURL = resourcePath.get('gamedayVideoLink', { game_pk: gamePk, languagePath });
    if (!videoURL) {
        return;
    }
    return {
        id: 'video',
        labelKey: 'button_video',
        labelFallback: 'Video',
        url: videoURL,
        handlers: {
            onClick: (e) => trackButtonClick(e)
        }
    };
};

const storyButton = ({ storyUrl, deviceInteraction, trackButtonClick }) => {
    if (!storyUrl) {
        return;
    }
    return {
        id: 'story',
        labelKey: 'button_story',
        labelFallback: 'Story',
        url: `https://${storyUrl}`,
        handlers: {
            onClick: (e) => {
                if (deviceInteraction.webview({ url: storyUrl })) {
                    e.preventDefault();
                }
                trackButtonClick(e);
            }
        }
    };
};

const ticketsButton = {
    id: 'tickets'
};

const buttonMap = {
    preview: ({ gamePk, languagePath, deviceInteraction, trackButtonClick }) => previewButton({ gamePk, languagePath, deviceInteraction, trackButtonClick }),
    tickets: () => ticketsButton,
    mlbtv: ({ gamePk, language, gamedayContext, gameDate, broadcastInfo: { tvFeeds = [] } = {}, homeAwayContext, deviceInteraction, trackButtonClick }) => mlbtvButton({ gamePk, language, gamedayContext, gameDate, tvFeeds, homeAwayContext, deviceInteraction, trackButtonClick }),
    audio: ({ gamePk, broadcastInfo: { audioFeed = [] } = {}, gamedayContext, deviceInteraction, trackButtonClick, language }) => audioButton({ gamePk, audioFeed, gamedayContext, deviceInteraction, trackButtonClick, language }),
    gameday: ({ gamePk, languagePath, deviceInteraction, trackButtonClick }) => gamedayButton({ gamePk, languagePath, deviceInteraction, trackButtonClick }),
    videos: ({ gamePk, languagePath, trackButtonClick }) => videoButton({ gamePk, languagePath, trackButtonClick }),
    wrap: ({ gamePk, languagePath, trackButtonClick }) => wrapButton({ gamePk, languagePath, trackButtonClick }),
    box: ({ gamePk, languagePath, trackButtonClick }) => boxButton({ gamePk, languagePath, trackButtonClick }),
    story: ({ storyUrl, deviceInteraction, trackButtonClick }) => storyButton({ storyUrl, deviceInteraction, trackButtonClick })
};

export const getProductButtons = () => {
    const { gameState, ...gameContext } = useSelector(getGameContextState);
    const { deviceInteraction } = useSelector(getDeviceInteraction);
    const Tracking = useTracking();
    const componentName = gameState === 'P' ? ['Preview'] : gameState === 'L' ? ['Live'] : ['Final'];
    const trackButtonClick = useCallback((event) => {
        const text = event?.currentTarget.innerText ?? '';
        const activity = `${text} Click`;
        const cfg = {
            componentName,
            text,
            activity,
            linkName: activity
        };
        Tracking.track.track_click(cfg);
    }, [Tracking, gameState]);
    const languagePath = getLanguagePath(gameContext.language);

    return buttonConfigs[gameState]?.reduce((acc, buttonType) => {
        const productButton = buttonMap[buttonType]({ ...gameContext, languagePath, deviceInteraction, trackButtonClick });
        if (productButton) {
            acc.push(productButton);
        }
        return acc;
    }, []);
};
